import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { api } from '../lib/api';

type InfluencerRow = {
  id: string;
  code: string;
  name: string;
  active: boolean;
  signups: number;
  communityDiscountPct?: number;
  commissionPct?: number;
  pendingCommissionCents?: number;
  estimatedCommissionCents?: number;
  confirmedCommissionCents?: number;
  paidCommissionCents?: number;
  grossCents?: number;
  netCents?: number;
  shareUrl?: string;
  appleOfferCode?: string | null;
  appleOfferIdentifier?: string | null;
};

type Summary = {
  generatedAt: string;
  users: { total: number; last7d: number; last30d: number; stripe: number; apple: number };
  earlyBird: {
    totalSlots: number;
    claimed: number;
    remaining: number;
  };
  engagement: {
    month2Eligible: number;
    month2Applied: number;
  };
  influencers: InfluencerRow[];
  funnel?: {
    code: string;
    name: string;
    uniqueVisitors: number;
    signups: number;
    paid: number;
    visitToSignupPct: number;
    visitToPaidPct: number;
  }[];
  database: { sizeMb: number; status: string; engine: string };
  commissionRules?: { stripeFee: string; influencerPctOfNet: number; net30Note: string };
};

type EarningsPayload = {
  pendingCount: number;
  pendingCommissionCents: number;
  pendingGrossCents: number;
  pendingNetCents?: number;
  estimatedCount?: number;
  estimatedCommissionCents?: number;
  confirmedCount?: number;
  confirmedCommissionCents?: number;
  rows: {
    id: string;
    commissionCents: number;
    grossCents: number;
    netCents?: number;
    stripeFeeCents?: number;
    status: string;
    createdAt: string;
    payoutDueOn?: string | null;
    payoutPeriodKey?: string | null;
    billingSource?: string;
    isFirstMonth?: boolean;
    isEstimate?: boolean;
    note?: string | null;
    influencer: { code: string; name: string };
    user: { email: string };
  }[];
};

type PayoutLine = {
  influencerId: string;
  code: string;
  name: string;
  signupCount: number;
  earningCount: number;
  grossCents: number;
  stripeFeeCents: number;
  netCents: number;
  commissionCents: number;
  stripeCommissionCents?: number;
  appleCommissionCents?: number;
};

type UpcomingPayload = {
  achNote: string;
  feeModel: string;
  periods: {
    key: string;
    label: string;
    start: string;
    end: string;
    dueOn: string;
    overdue: boolean;
    totalCommissionCents: number;
    totalGrossCents: number;
    totalNetCents: number;
    totalStripeFeeCents: number;
    totalSignups: number;
    influencers: PayoutLine[];
  }[];
};

type HistoryPayload = {
  achNote: string;
  batches: {
    id: string;
    periodLabel: string;
    dueOn: string;
    status: string;
    signupCount: number;
    commissionCents: number;
    netCents: number;
    grossCents: number;
    stripeFeeCents: number;
    achInitiatedOn: string | null;
    note: string | null;
    influencer: { code: string; name: string };
  }[];
};

type TeamUser = {
  id: string;
  email: string;
  displayName: string | null;
  execDashboardAccess: boolean;
  aiUnlimited?: boolean;
  isOwner?: boolean;
};

type MaxedAiCost = {
  days: number;
  caps: { mealSearches: number; coachMessages: number; formChecks: number };
  typicalUsdPerDay: number;
  typicalUsdPerMonth: number;
  worstUsdPerDay: number;
  worstUsdPerMonth: number;
  typicalLines: { feature: string; perDay: number; model: string; note: string }[];
  worstLines: { feature: string; perDay: number; model: string; note: string }[];
  summary: string;
};

type AiCosts = {
  lookbackDays: number;
  pricing: { inputUsdPerMillion: number; outputUsdPerMillion: number; modelNote: string };
  fleet: {
    usersWithUsage: number;
    totalPromptTokens: number;
    totalCompletionTokens: number;
    totalTokens: number;
    totalUsd: number;
    avgUsdPerCalendarDay: number;
  };
  averageUserUsdPerDay: number;
  mostActiveUser: {
    userId: string;
    email: string | null;
    displayName: string | null;
    totalTokens: number;
    totalUsd: number;
    usdPerDay: number;
    requestCount: number;
    byFeature: { feature: string; tokens: number; usd: number; requests: number }[];
  } | null;
  topUsers: {
    userId: string;
    email: string | null;
    displayName: string | null;
    totalTokens: number;
    totalUsd: number;
    usdPerDay: number;
    requestCount: number;
  }[];
  dailyFleet: { date: string; usd: number; tokens: number; requests: number }[];
  maxedUser?: MaxedAiCost;
};

function usd(cents: number | undefined) {
  return `$${((cents ?? 0) / 100).toFixed(2)}`;
}

function usdMoney(n: number | undefined) {
  return `$${(n ?? 0).toFixed(4)}`;
}

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'America/New_York',
  });
}

export function ExecutivePage() {
  const { user, token, loading } = useAuth();
  const [data, setData] = useState<Summary | null>(null);
  const [earnings, setEarnings] = useState<EarningsPayload | null>(null);
  const [upcoming, setUpcoming] = useState<UpcomingPayload | null>(null);
  const [history, setHistory] = useState<HistoryPayload | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [searchQ, setSearchQ] = useState('');
  const [searchHits, setSearchHits] = useState<TeamUser[]>([]);
  const [team, setTeam] = useState<{ owners: string[]; granted: TeamUser[] } | null>(null);
  const [aiCosts, setAiCosts] = useState<AiCosts | null>(null);
  const [aiUnlimitedUsers, setAiUnlimitedUsers] = useState<TeamUser[]>([]);
  const [aiSearchQ, setAiSearchQ] = useState('');
  const [aiSearchHits, setAiSearchHits] = useState<TeamUser[]>([]);

  async function load() {
    setErr(null);
    try {
      const [s, e, u, h, t, a, unlim] = await Promise.all([
        api<Summary>('/api/executive/summary'),
        api<EarningsPayload>('/api/executive/earnings'),
        api<UpcomingPayload>('/api/executive/payouts/upcoming'),
        api<HistoryPayload>('/api/executive/payouts/history'),
        api<{ owners: string[]; granted: TeamUser[] }>('/api/executive/team'),
        api<AiCosts>('/api/executive/ai-costs?days=30'),
        api<{ users: TeamUser[] }>('/api/executive/ai-unlimited'),
      ]);
      setData(s);
      setEarnings(e);
      setUpcoming(u);
      setHistory(h);
      setTeam(t);
      setAiCosts(a);
      setAiUnlimitedUsers(unlim.users);
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : 'Failed to load');
      setData(null);
    }
  }

  useEffect(() => {
    if (!token) return;
    void load();
  }, [token]);

  if (loading) {
    return <div className="app-body flex min-h-screen items-center justify-center text-sm text-zinc-500">Loading…</div>;
  }
  if (!user) return <Navigate to="/login?next=/executive" replace />;

  async function createInfluencer(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await api('/api/executive/influencers', {
        method: 'POST',
        body: JSON.stringify({ code, name }),
      });
      setCode('');
      setName('');
      await load();
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : 'Could not create influencer');
    } finally {
      setBusy(false);
    }
  }

  async function markPeriodPaid(influencerId: string, periodKey: string) {
    setBusy(true);
    setErr(null);
    try {
      await api('/api/executive/payouts/mark-paid', {
        method: 'POST',
        body: JSON.stringify({ influencerId, periodKey }),
      });
      await load();
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : 'Could not mark period paid');
    } finally {
      setBusy(false);
    }
  }

  async function searchUsers(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const res = await api<{ users: TeamUser[] }>(
        `/api/executive/users/search?q=${encodeURIComponent(searchQ.trim())}`,
      );
      setSearchHits(res.users);
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : 'Search failed');
    } finally {
      setBusy(false);
    }
  }

  async function grantAccess(userId: string) {
    setBusy(true);
    setErr(null);
    try {
      await api('/api/executive/team/grant', { method: 'POST', body: JSON.stringify({ userId }) });
      await load();
      setSearchHits((prev) =>
        prev.map((u) => (u.id === userId ? { ...u, execDashboardAccess: true } : u)),
      );
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : 'Could not grant access');
    } finally {
      setBusy(false);
    }
  }

  async function searchAiUsers(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const res = await api<{ users: TeamUser[] }>(
        `/api/executive/users/search?q=${encodeURIComponent(aiSearchQ.trim())}`,
      );
      setAiSearchHits(res.users);
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : 'Search failed');
    } finally {
      setBusy(false);
    }
  }

  async function setAiUnlimited(userId: string, unlimited: boolean) {
    setBusy(true);
    setErr(null);
    try {
      const row = await api<TeamUser>('/api/executive/ai-unlimited', {
        method: 'POST',
        body: JSON.stringify({ userId, unlimited }),
      });
      setAiSearchHits((prev) => prev.map((u) => (u.id === userId ? { ...u, aiUnlimited: row.aiUnlimited } : u)));
      await load();
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : 'Could not update unlimited AI');
    } finally {
      setBusy(false);
    }
  }

  async function revokeAccess(userId: string) {
    setBusy(true);
    setErr(null);
    try {
      await api('/api/executive/team/revoke', { method: 'POST', body: JSON.stringify({ userId }) });
      await load();
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : 'Could not revoke access');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="app-body min-h-screen px-4 py-8 text-zinc-100 sm:px-8">
      <div className="mx-auto max-w-5xl">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-wider text-zinc-500">Executive</p>
            <h1 className="text-2xl font-bold">Ops dashboard</h1>
          </div>
          <div className="flex gap-3 text-sm">
            <button
              type="button"
              onClick={() => void load()}
              className="rounded-lg border border-white/15 px-3 py-2 hover:bg-white/5"
            >
              Refresh
            </button>
            <Link to="/" className="rounded-lg border border-white/15 px-3 py-2 hover:bg-white/5">
              Home
            </Link>
          </div>
        </div>

        {err ? (
          <p className="mt-4 rounded-xl border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-100">
            {err}
          </p>
        ) : null}

        {!data ? (
          <p className="mt-8 text-sm text-zinc-500">Loading metrics…</p>
        ) : (
          <>
            <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {[
                ['Users', data.users.total],
                ['New 7d', data.users.last7d],
                ['Stripe', data.users.stripe],
                ['Early bird left', data.earlyBird.remaining],
                ['Total owed', usd(earnings?.pendingCommissionCents)],
                [
                  'Confirmed charged',
                  usd(earnings?.confirmedCommissionCents ?? 0),
                ],
                [
                  'In trial (est.)',
                  usd(earnings?.estimatedCommissionCents ?? 0),
                ],
                ['Pending rows', earnings?.pendingCount ?? 0],
              ].map(([label, value]) => (
                <div key={String(label)} className="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3">
                  <p className="text-[11px] uppercase tracking-wider text-zinc-500">{label}</p>
                  <p className="mt-1 text-2xl font-semibold tabular-nums">{value}</p>
                </div>
              ))}
            </div>

            <section className="mt-10">
              <h2 className="text-lg font-semibold">AI token costs</h2>
              <p className="mt-1 text-sm text-zinc-400">
                Last {aiCosts?.lookbackDays ?? 30} days · gpt-4o-mini pricing ($
                {aiCosts?.pricing.inputUsdPerMillion ?? 0.15}/1M in · $
                {aiCosts?.pricing.outputUsdPerMillion ?? 0.6}/1M out). Daily averages divide by calendar
                days in the window (quiet days count as $0).
              </p>
              {aiCosts?.maxedUser ? (
                <div className="mt-4 rounded-2xl border border-amber-400/25 bg-amber-500/[0.07] px-4 py-4">
                  <p className="text-sm font-semibold text-amber-100">Maxed paid user (daily caps, every day)</p>
                  <p className="mt-1 text-sm text-amber-100/80">{aiCosts.maxedUser.summary}</p>
                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    <div>
                      <p className="text-[11px] uppercase tracking-wider text-amber-200/70">4-food meals · 1 batched 4o guess</p>
                      <p className="mt-1 text-2xl font-semibold tabular-nums text-amber-50">
                        ${aiCosts.maxedUser.typicalUsdPerMonth.toFixed(2)}
                        <span className="ml-1 text-sm font-normal text-amber-200/70">/ 30 days</span>
                      </p>
                      <p className="mt-0.5 text-xs text-amber-200/60">
                        ${aiCosts.maxedUser.typicalUsdPerDay.toFixed(4)}/day
                      </p>
                    </div>
                    <div>
                      <p className="text-[11px] uppercase tracking-wider text-amber-200/70">8-food meals · 1 batched 4o guess</p>
                      <p className="mt-1 text-2xl font-semibold tabular-nums text-amber-50">
                        ${aiCosts.maxedUser.worstUsdPerMonth.toFixed(2)}
                        <span className="ml-1 text-sm font-normal text-amber-200/70">/ 30 days</span>
                      </p>
                      <p className="mt-0.5 text-xs text-amber-200/60">
                        ${aiCosts.maxedUser.worstUsdPerDay.toFixed(4)}/day
                      </p>
                    </div>
                  </div>
                  <ul className="mt-3 space-y-1 text-xs text-amber-100/70">
                    {aiCosts.maxedUser.typicalLines.map((line) => (
                      <li key={line.feature}>
                        {line.feature}: ${line.perDay.toFixed(4)}/day · {line.model} — {line.note}
                      </li>
                    ))}
                  </ul>
                  <p className="mt-2 text-[11px] text-amber-200/50">
                    Caps: {aiCosts.maxedUser.caps.mealSearches} meals · {aiCosts.maxedUser.caps.coachMessages}{' '}
                    coach · {aiCosts.maxedUser.caps.formChecks} form checks per day. Unlimited accounts are
                    outside this estimate.
                  </p>
                </div>
              ) : null}
              {!aiCosts ? (
                <p className="mt-3 text-sm text-zinc-500">Loading AI costs…</p>
              ) : (
                <>
                  <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    {[
                      ['Most active $/day', usdMoney(aiCosts.mostActiveUser?.usdPerDay)],
                      ['Avg user $/day', usdMoney(aiCosts.averageUserUsdPerDay)],
                      ['Fleet $/day', usdMoney(aiCosts.fleet.avgUsdPerCalendarDay)],
                      ['Fleet total (window)', usdMoney(aiCosts.fleet.totalUsd)],
                    ].map(([label, value]) => (
                      <div key={String(label)} className="rounded-2xl border border-cyan-500/20 bg-cyan-500/[0.06] px-4 py-3">
                        <p className="text-[11px] uppercase tracking-wider text-cyan-200/70">{label}</p>
                        <p className="mt-1 text-2xl font-semibold tabular-nums text-cyan-50">{value}</p>
                      </div>
                    ))}
                  </div>

                  {aiCosts.mostActiveUser ? (
                    <div className="mt-4 rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-4">
                      <p className="text-sm font-semibold text-zinc-100">Most active AI user</p>
                      <p className="mt-1 text-sm text-zinc-400">
                        {aiCosts.mostActiveUser.displayName || aiCosts.mostActiveUser.email || aiCosts.mostActiveUser.userId}
                        {' · '}
                        {aiCosts.mostActiveUser.requestCount} requests ·{' '}
                        {aiCosts.mostActiveUser.totalTokens.toLocaleString()} tokens ·{' '}
                        {usdMoney(aiCosts.mostActiveUser.totalUsd)} total
                      </p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {aiCosts.mostActiveUser.byFeature.map((f) => (
                          <span
                            key={f.feature}
                            className="rounded-full border border-white/10 bg-black/20 px-3 py-1 text-xs text-zinc-300"
                          >
                            {f.feature}: {f.requests}× · {usdMoney(f.usd)}
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <p className="mt-4 text-sm text-zinc-500">
                      No AI usage recorded yet. Costs appear after coach chat / meal estimate calls on this build.
                    </p>
                  )}

                  {aiCosts.topUsers.length > 0 ? (
                    <div className="mt-4 overflow-x-auto rounded-2xl border border-white/10">
                      <table className="min-w-full text-left text-sm">
                        <thead className="border-b border-white/10 text-[11px] uppercase tracking-wider text-zinc-500">
                          <tr>
                            <th className="px-3 py-2 font-medium">User</th>
                            <th className="px-3 py-2 font-medium">Requests</th>
                            <th className="px-3 py-2 font-medium">Tokens</th>
                            <th className="px-3 py-2 font-medium">Total</th>
                            <th className="px-3 py-2 font-medium">$/day avg</th>
                          </tr>
                        </thead>
                        <tbody>
                          {aiCosts.topUsers.map((u) => (
                            <tr key={u.userId} className="border-b border-white/5 last:border-0">
                              <td className="px-3 py-2 text-zinc-200">
                                {u.displayName || u.email || u.userId.slice(0, 8)}
                              </td>
                              <td className="px-3 py-2 tabular-nums text-zinc-400">{u.requestCount}</td>
                              <td className="px-3 py-2 tabular-nums text-zinc-400">
                                {u.totalTokens.toLocaleString()}
                              </td>
                              <td className="px-3 py-2 tabular-nums text-zinc-300">{usdMoney(u.totalUsd)}</td>
                              <td className="px-3 py-2 tabular-nums text-cyan-100">{usdMoney(u.usdPerDay)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : null}
                </>
              )}
            </section>

            <section className="mt-10">
              <h2 className="text-lg font-semibold">Influencer payouts (1st &amp; 15th)</h2>
              <p className="mt-1 text-sm text-zinc-400">
                {upcoming?.feeModel ??
                  'First paid month only. 10% of net after fees. Stripe trials show estimated owed until the first charge.'}
              </p>
              <p className="mt-1 text-sm text-zinc-500">
                {upcoming?.achNote ?? data.commissionRules?.net30Note}
              </p>
              <p className="mt-2 text-xs text-zinc-500">
                Example: Aug 24–Sep 1 → pay Oct 1 · Sep 2–15 → pay Oct 15 · Sep 16–Oct 1 → pay Nov 1
              </p>

              {(upcoming?.periods ?? []).map((p) => (
                <div
                  key={p.key}
                  className="mt-5 rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-4"
                >
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <p className="text-sm font-semibold text-white">
                        {p.overdue ? 'Overdue · ' : 'Due '}
                        {fmtDate(p.dueOn)}
                      </p>
                      <p className="mt-1 text-xs text-zinc-500">
                        Signups {fmtDate(p.start)} – {fmtDate(p.end)} · ACH leaves checking on{' '}
                        {fmtDate(p.dueOn)} (often lands 1–4 business days later)
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-semibold tabular-nums text-sky-300">
                        {usd(p.totalCommissionCents)}
                      </p>
                      <p className="text-xs text-zinc-500">
                        {p.totalSignups} signups · net {usd(p.totalNetCents)} after Stripe
                      </p>
                    </div>
                  </div>

                  <div className="mt-4 overflow-x-auto">
                    <table className="w-full min-w-[40rem] text-left text-sm">
                      <thead className="text-xs uppercase tracking-wider text-zinc-500">
                        <tr>
                          <th className="py-2 pr-3">Influencer</th>
                          <th className="py-2 pr-3">Signups</th>
                          <th className="py-2 pr-3">Gross</th>
                          <th className="py-2 pr-3">Stripe fee</th>
                          <th className="py-2 pr-3">Net</th>
                          <th className="py-2 pr-3">Payout</th>
                          <th className="py-2 pr-3">Stripe / Apple</th>
                          <th className="py-2">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {p.influencers.map((line) => (
                          <tr key={line.influencerId} className="border-t border-white/10">
                            <td className="py-2 pr-3">
                              <span className="font-mono text-zinc-200">{line.code}</span>
                              <span className="ml-2 text-zinc-500">{line.name}</span>
                            </td>
                            <td className="py-2 pr-3 tabular-nums">{line.signupCount}</td>
                            <td className="py-2 pr-3 tabular-nums">{usd(line.grossCents)}</td>
                            <td className="py-2 pr-3 tabular-nums text-zinc-500">
                              {usd(line.stripeFeeCents)}
                            </td>
                            <td className="py-2 pr-3 tabular-nums">{usd(line.netCents)}</td>
                            <td className="py-2 pr-3 tabular-nums font-semibold text-sky-200">
                              {usd(line.commissionCents)}
                            </td>
                            <td className="py-2 pr-3 text-xs text-zinc-500">
                              {usd(line.stripeCommissionCents)} / {usd(line.appleCommissionCents)}
                            </td>
                            <td className="py-2">
                              {line.commissionCents > 0 ? (
                                <button
                                  type="button"
                                  disabled={busy}
                                  onClick={() => void markPeriodPaid(line.influencerId, p.key)}
                                  className="rounded-md border border-emerald-500/40 px-2 py-1 text-xs text-emerald-200 hover:bg-emerald-500/10 disabled:opacity-50"
                                >
                                  Mark ACH sent
                                </button>
                              ) : (
                                <span className="text-xs text-zinc-600">No paid invoices yet</span>
                              )}
                            </td>
                          </tr>
                        ))}
                        {!p.influencers.length ? (
                          <tr>
                            <td colSpan={8} className="py-3 text-zinc-500">
                              No influencer activity in this window yet
                            </td>
                          </tr>
                        ) : null}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}

              {!upcoming?.periods?.length ? (
                <p className="mt-4 text-sm text-zinc-500">No upcoming payout windows with activity.</p>
              ) : null}
            </section>

            <section className="mt-10">
              <h2 className="text-lg font-semibold">Payout history</h2>
              <p className="mt-1 text-sm text-zinc-400">Every ACH batch you mark as sent.</p>
              <div className="mt-4 overflow-x-auto">
                <table className="w-full min-w-[40rem] text-left text-sm">
                  <thead className="text-xs uppercase tracking-wider text-zinc-500">
                    <tr>
                      <th className="py-2 pr-3">Due</th>
                      <th className="py-2 pr-3">Influencer</th>
                      <th className="py-2 pr-3">Period</th>
                      <th className="py-2 pr-3">Signups</th>
                      <th className="py-2 pr-3">Paid out</th>
                      <th className="py-2">Sent</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(history?.batches ?? []).map((b) => (
                      <tr key={b.id} className="border-t border-white/10">
                        <td className="py-2 pr-3 tabular-nums">{fmtDate(b.dueOn)}</td>
                        <td className="py-2 pr-3 font-mono">{b.influencer.code}</td>
                        <td className="py-2 pr-3 text-xs text-zinc-400">{b.periodLabel}</td>
                        <td className="py-2 pr-3 tabular-nums">{b.signupCount}</td>
                        <td className="py-2 pr-3 tabular-nums font-semibold">{usd(b.commissionCents)}</td>
                        <td className="py-2 text-xs text-zinc-500">
                          {b.achInitiatedOn ? fmtDate(b.achInitiatedOn) : b.status}
                        </td>
                      </tr>
                    ))}
                    {!history?.batches?.length ? (
                      <tr>
                        <td colSpan={6} className="py-3 text-zinc-500">
                          No payout batches recorded yet
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="mt-10">
              <h2 className="text-lg font-semibold">Team access</h2>
              <p className="mt-1 text-sm text-zinc-400">
                Search for Dani, your accountant, etc. and grant /executive access. Owners (
                {(team?.owners ?? []).join(', ') || 'EXEC_ADMIN_EMAILS'}) always have access.
              </p>
              <form onSubmit={(e) => void searchUsers(e)} className="mt-4 flex flex-wrap gap-2">
                <input
                  value={searchQ}
                  onChange={(e) => setSearchQ(e.target.value)}
                  placeholder="Search email or name"
                  className="min-w-[14rem] flex-1 rounded-lg border border-white/15 bg-black/30 px-3 py-2 text-sm"
                />
                <button
                  type="submit"
                  disabled={busy || searchQ.trim().length < 2}
                  className="rounded-lg bg-sky-400 px-4 py-2 text-sm font-semibold text-zinc-950 disabled:opacity-50"
                >
                  Search
                </button>
              </form>
              {searchHits.length ? (
                <ul className="mt-3 space-y-2">
                  {searchHits.map((u) => (
                    <li
                      key={u.id}
                      className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-white/10 px-3 py-2 text-sm"
                    >
                      <div>
                        <p className="font-medium">{u.email}</p>
                        <p className="text-xs text-zinc-500">
                          {u.displayName || 'No display name'}
                          {u.isOwner ? ' · owner' : u.execDashboardAccess ? ' · has access' : ''}
                        </p>
                      </div>
                      {u.isOwner ? (
                        <span className="text-xs text-zinc-500">Owner</span>
                      ) : u.execDashboardAccess ? (
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => void revokeAccess(u.id)}
                          className="rounded-md border border-rose-500/40 px-2 py-1 text-xs text-rose-200"
                        >
                          Revoke
                        </button>
                      ) : (
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => void grantAccess(u.id)}
                          className="rounded-md border border-emerald-500/40 px-2 py-1 text-xs text-emerald-200"
                        >
                          Grant access
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              ) : null}
              {(team?.granted ?? []).length ? (
                <div className="mt-4">
                  <p className="text-xs uppercase tracking-wider text-zinc-500">Currently granted</p>
                  <ul className="mt-2 space-y-1 text-sm text-zinc-300">
                    {team!.granted.map((u) => (
                      <li key={u.id} className="flex items-center justify-between gap-2">
                        <span>{u.email}</span>
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => void revokeAccess(u.id)}
                          className="text-xs text-rose-300 underline"
                        >
                          Revoke
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </section>

            <section className="mt-10">
              <h2 className="text-lg font-semibold">Unlimited AI</h2>
              <p className="mt-1 text-sm text-zinc-400">
                Look up an account by email and turn off daily meal / coach / form caps. Owner access only to
                change. Unlimited users still count toward token cost on the chart above.
              </p>
              <form onSubmit={(e) => void searchAiUsers(e)} className="mt-4 flex flex-wrap gap-2">
                <input
                  value={aiSearchQ}
                  onChange={(e) => setAiSearchQ(e.target.value)}
                  placeholder="Search email or name"
                  className="min-w-[14rem] flex-1 rounded-lg border border-white/15 bg-black/30 px-3 py-2 text-sm"
                />
                <button
                  type="submit"
                  disabled={busy || aiSearchQ.trim().length < 2}
                  className="rounded-lg bg-sky-400 px-4 py-2 text-sm font-semibold text-zinc-950 disabled:opacity-50"
                >
                  Search
                </button>
              </form>
              {aiSearchHits.length ? (
                <ul className="mt-3 space-y-2">
                  {aiSearchHits.map((u) => (
                    <li
                      key={u.id}
                      className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-white/10 px-3 py-2 text-sm"
                    >
                      <div>
                        <p className="font-medium">{u.email}</p>
                        <p className="text-xs text-zinc-500">
                          {u.displayName || 'No display name'}
                          {u.aiUnlimited ? ' · unlimited AI' : ' · daily caps'}
                        </p>
                      </div>
                      {u.aiUnlimited ? (
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => void setAiUnlimited(u.id, false)}
                          className="rounded-md border border-rose-500/40 px-2 py-1 text-xs text-rose-200"
                        >
                          Remove unlimited
                        </button>
                      ) : (
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => void setAiUnlimited(u.id, true)}
                          className="rounded-md border border-emerald-500/40 px-2 py-1 text-xs text-emerald-200"
                        >
                          Grant unlimited
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              ) : null}
              {aiUnlimitedUsers.length ? (
                <div className="mt-4">
                  <p className="text-xs uppercase tracking-wider text-zinc-500">Currently unlimited</p>
                  <ul className="mt-2 space-y-1 text-sm text-zinc-300">
                    {aiUnlimitedUsers.map((u) => (
                      <li key={u.id} className="flex flex-wrap items-center justify-between gap-2">
                        <span>
                          {u.email}
                          {u.displayName ? ` · ${u.displayName}` : ''}
                        </span>
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => void setAiUnlimited(u.id, false)}
                          className="text-xs text-rose-200 hover:underline"
                        >
                          Remove
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <p className="mt-3 text-sm text-zinc-500">No unlimited AI accounts yet.</p>
              )}
            </section>

            <section className="mt-10">
              <h2 className="text-lg font-semibold">Influencer funnel</h2>
              <div className="mt-4 overflow-x-auto">
                <table className="w-full min-w-[40rem] text-left text-sm">
                  <thead className="text-xs uppercase tracking-wider text-zinc-500">
                    <tr>
                      <th className="py-2 pr-3">Code</th>
                      <th className="py-2 pr-3">Visitors</th>
                      <th className="py-2 pr-3">Signups</th>
                      <th className="py-2 pr-3">Paid</th>
                      <th className="py-2 pr-3">Visit→Signup</th>
                      <th className="py-2">Visit→Paid</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.funnel ?? []).map((f) => (
                      <tr key={f.code} className="border-t border-white/10">
                        <td className="py-2 pr-3 font-mono">{f.code}</td>
                        <td className="py-2 pr-3 tabular-nums">{f.uniqueVisitors}</td>
                        <td className="py-2 pr-3 tabular-nums">{f.signups}</td>
                        <td className="py-2 pr-3 tabular-nums">{f.paid}</td>
                        <td className="py-2 pr-3 tabular-nums">{f.visitToSignupPct}%</td>
                        <td className="py-2 tabular-nums">{f.visitToPaidPct}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="mt-10">
              <h2 className="text-lg font-semibold">Influencer codes</h2>
              <p className="mt-1 text-sm text-zinc-400">
                Influencers earn <strong className="text-zinc-200">10% of net after platform fees</strong> on the{' '}
                <strong className="text-zinc-200">first paid month only</strong> (50% off → roughly half a full-price
                cut). Stripe and Apple both count.
              </p>
              <div className="mt-3 rounded-xl border border-amber-500/20 bg-amber-500/5 px-3 py-3 text-sm text-zinc-300">
                <p className="font-semibold text-amber-100">Apple offer codes (App Store Connect)</p>
                <ol className="mt-2 list-decimal space-y-1 pl-5 text-xs text-zinc-400">
                  <li>Subscriptions → your Pro sub → Subscription Prices → Offer Codes → create a custom code</li>
                  <li>Use a memorable code per influencer (same as their web code, e.g. DANI50)</li>
                  <li>Set discount to match founders (e.g. 50% for 1 month) · new subscribers</li>
                  <li>Save the code on the influencer row below — redemptions attribute + pay out (after Apple&apos;s 30%)</li>
                </ol>
              </div>
              <p className="mt-2 break-all font-mono text-xs text-sky-300/90">
                Founders link: https://peakperformanceiq.com/signup?founders=1
              </p>
              <form onSubmit={(e) => void createInfluencer(e)} className="mt-4 flex flex-wrap gap-2">
                <input
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  placeholder="CODE"
                  className="rounded-lg border border-white/15 bg-black/30 px-3 py-2 text-sm"
                />
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Display name"
                  className="rounded-lg border border-white/15 bg-black/30 px-3 py-2 text-sm"
                />
                <button
                  type="submit"
                  disabled={busy || !code || !name}
                  className="rounded-lg bg-sky-400 px-4 py-2 text-sm font-semibold text-zinc-950 disabled:opacity-50"
                >
                  Create code
                </button>
              </form>
              <div className="mt-4 overflow-x-auto">
                <table className="w-full min-w-[48rem] text-left text-sm">
                  <thead className="text-xs uppercase tracking-wider text-zinc-500">
                    <tr>
                      <th className="py-2 pr-3">Code</th>
                      <th className="py-2 pr-3">Name</th>
                      <th className="py-2 pr-3">Signups</th>
                      <th className="py-2 pr-3">You owe</th>
                      <th className="py-2 pr-3">Apple offer</th>
                      <th className="py-2">Share</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.influencers.map((i) => (
                      <tr key={i.id} className="border-t border-white/10 align-top">
                        <td className="py-2 pr-3 font-mono">{i.code}</td>
                        <td className="py-2 pr-3">{i.name}</td>
                        <td className="py-2 pr-3 tabular-nums">{i.signups}</td>
                        <td className="py-2 pr-3">
                          <p className="tabular-nums font-semibold text-sky-300">
                            {usd(i.pendingCommissionCents)}
                          </p>
                          {(i.estimatedCommissionCents ?? 0) > 0 ||
                          (i.confirmedCommissionCents ?? 0) > 0 ? (
                            <p className="mt-0.5 text-[11px] leading-snug text-zinc-500">
                              {(i.confirmedCommissionCents ?? 0) > 0
                                ? `${usd(i.confirmedCommissionCents)} charged`
                                : null}
                              {(i.confirmedCommissionCents ?? 0) > 0 &&
                              (i.estimatedCommissionCents ?? 0) > 0
                                ? ' · '
                                : null}
                              {(i.estimatedCommissionCents ?? 0) > 0
                                ? `${usd(i.estimatedCommissionCents)} in trial`
                                : null}
                            </p>
                          ) : (
                            <p className="mt-0.5 text-[11px] text-zinc-600">No commissions yet</p>
                          )}
                        </td>
                        <td className="py-2 pr-3">
                          <AppleOfferEditor
                            influencerId={i.id}
                            initial={i.appleOfferCode ?? i.code}
                            busy={busy}
                            onSaved={() => void load()}
                            onError={(m) => setErr(m)}
                            setBusy={setBusy}
                          />
                        </td>
                        <td className="py-2 break-all font-mono text-xs text-zinc-400">{i.shareUrl}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="mt-10">
              <h2 className="text-lg font-semibold">Commission ledger</h2>
              <div className="mt-3 grid gap-2 sm:grid-cols-3">
                <div className="rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2">
                  <p className="text-[11px] uppercase tracking-wider text-zinc-500">Total owed</p>
                  <p className="text-xl font-semibold tabular-nums text-sky-300">
                    {usd(earnings?.pendingCommissionCents)}
                  </p>
                  <p className="text-[11px] text-zinc-500">
                    {earnings?.pendingCount ?? 0} rows · net after fees {usd(earnings?.pendingNetCents)}
                  </p>
                </div>
                <div className="rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2">
                  <p className="text-[11px] uppercase tracking-wider text-zinc-500">Confirmed</p>
                  <p className="text-xl font-semibold tabular-nums">
                    {usd(earnings?.confirmedCommissionCents)}
                  </p>
                  <p className="text-[11px] text-zinc-500">
                    {earnings?.confirmedCount ?? 0} first charges collected
                  </p>
                </div>
                <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 px-3 py-2">
                  <p className="text-[11px] uppercase tracking-wider text-amber-200/70">In trial (est.)</p>
                  <p className="text-xl font-semibold tabular-nums text-amber-100">
                    {usd(earnings?.estimatedCommissionCents)}
                  </p>
                  <p className="text-[11px] text-zinc-500">
                    {earnings?.estimatedCount ?? 0} unlocked · adjusts when Stripe bills
                  </p>
                </div>
              </div>
              <div className="mt-4 overflow-x-auto">
                <table className="w-full min-w-[48rem] text-left text-sm">
                  <thead className="text-xs uppercase tracking-wider text-zinc-500">
                    <tr>
                      <th className="py-2 pr-3">When</th>
                      <th className="py-2 pr-3">Influencer</th>
                      <th className="py-2 pr-3">User</th>
                      <th className="py-2 pr-3">Gross</th>
                      <th className="py-2 pr-3">Fee</th>
                      <th className="py-2 pr-3">Net</th>
                      <th className="py-2 pr-3">You owe</th>
                      <th className="py-2 pr-3">Status</th>
                      <th className="py-2">Pay by</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(earnings?.rows ?? []).map((row) => (
                      <tr key={row.id} className="border-t border-white/10">
                        <td className="py-2 pr-3 text-xs text-zinc-400">
                          {new Date(row.createdAt).toLocaleString()}
                        </td>
                        <td className="py-2 pr-3 font-mono">{row.influencer.code}</td>
                        <td className="py-2 pr-3">{row.user.email}</td>
                        <td className="py-2 pr-3 tabular-nums">{usd(row.grossCents)}</td>
                        <td className="py-2 pr-3 tabular-nums text-zinc-500">
                          {usd(row.stripeFeeCents)}
                        </td>
                        <td className="py-2 pr-3 tabular-nums">{usd(row.netCents)}</td>
                        <td className="py-2 pr-3 tabular-nums font-semibold text-sky-300">
                          {usd(row.commissionCents)}
                        </td>
                        <td className="py-2 pr-3 text-xs text-zinc-400">
                          {row.status === 'paid'
                            ? 'Paid'
                            : row.isEstimate || row.note?.includes('trial_estimate')
                              ? 'Trial · est.'
                              : 'Confirmed'}
                          {' · '}
                          {row.billingSource === 'apple' ? 'Apple' : 'Stripe'}
                          {row.note?.includes('first_month_50_off') ? ' · 50% off' : ''}
                        </td>
                        <td className="py-2 text-xs text-zinc-500">
                          {row.status === 'paid'
                            ? '—'
                            : row.payoutDueOn
                              ? fmtDate(row.payoutDueOn)
                              : '—'}
                        </td>
                      </tr>
                    ))}
                    {!earnings?.rows?.length ? (
                      <tr>
                        <td colSpan={9} className="py-3 text-zinc-500">
                          No commission rows yet
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </section>

            <p className="mt-10 text-xs text-zinc-600">
              Generated {new Date(data.generatedAt).toLocaleString()}
            </p>
          </>
        )}
      </div>
    </div>
  );
}

function AppleOfferEditor(props: {
  influencerId: string;
  initial: string;
  busy: boolean;
  setBusy: (v: boolean) => void;
  onSaved: () => void;
  onError: (m: string) => void;
}) {
  const [value, setValue] = useState(props.initial);
  useEffect(() => {
    setValue(props.initial);
  }, [props.initial]);

  async function save() {
    props.setBusy(true);
    try {
      await api(`/api/executive/influencers/${props.influencerId}`, {
        method: 'PATCH',
        body: JSON.stringify({ appleOfferCode: value.trim().toUpperCase() }),
      });
      props.onSaved();
    } catch (e) {
      props.onError(e instanceof Error ? e.message : 'Could not save Apple offer code');
    } finally {
      props.setBusy(false);
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-1">
      <input
        value={value}
        onChange={(e) => setValue(e.target.value.toUpperCase())}
        className="w-28 rounded border border-white/15 bg-black/40 px-2 py-1 font-mono text-xs"
      />
      <button
        type="button"
        disabled={props.busy || value.trim() === props.initial}
        onClick={() => void save()}
        className="rounded border border-white/20 px-2 py-1 text-[10px] text-zinc-300 disabled:opacity-40"
      >
        Save
      </button>
    </div>
  );
}
